'use client'

import React from 'react'
import { ErrorBoundary } from '@/components/common/ErrorMonitoringV2'
import { reportError } from '@/lib/report-error-v2'

interface GlobalErrorFallbackProps {
  error: Error
  resetError: () => void
}

function GlobalErrorFallback({ error, resetError }: GlobalErrorFallbackProps) {
  return (
    <div className="flex min-h-[60vh] w-full items-center justify-center px-6">
      <div className="max-w-2xl rounded-2xl border border-red-400/30 bg-red-950/40 p-8 text-red-100 shadow-2xl backdrop-blur-md">
        <div className="mb-4 text-sm uppercase tracking-[0.3em] text-red-300/80">Application Error</div>
        <h2 className="text-2xl font-semibold text-red-100">
          Something unexpected interrupted your experience.
        </h2>
        <p className="mt-3 text-sm text-red-200/80">
          {error?.message || 'An unknown error occurred. Our monitoring team has been notified.'}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={resetError}
            className="rounded-lg border border-red-300/40 bg-red-500/20 px-5 py-2 text-sm font-medium text-red-100 transition hover:border-red-200/70 hover:bg-red-500/30"
          >
            Try Again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg border border-red-200/40 px-5 py-2 text-sm font-medium text-red-200 transition hover:border-red-200 hover:bg-red-500/20"
          >
            Refresh Page
          </button>
        </div>
      </div>
    </div>
  )
}

interface AppErrorBoundaryProps {
  children: React.ReactNode
  componentName?: string
  storyId?: string
  chapterId?: string
  /** @deprecated use componentName */
  source?: string
}

export function AppErrorBoundary({
  children,
  componentName,
  storyId,
  chapterId,
  source
}: AppErrorBoundaryProps) {
  const resolvedComponentName = componentName ?? source ?? 'UnknownComponent'

  return (
    <ErrorBoundary
      fallback={GlobalErrorFallback}
      onError={(error, errorInfo) => {
        console.error(`[AppErrorBoundary:${resolvedComponentName}]`, error, errorInfo)

        reportError(error, {
          component: resolvedComponentName,
          operation: `render_${resolvedComponentName}`,
          ...(storyId && { storyId }),
          severity: 'high',
          category: 'component_error'
        })
      }}
    >
      {children}
    </ErrorBoundary>
  )
}

